import { useEffect, useState } from "react";

// Quanto tempo o cliente está esperando. É o dado que decide quem atender
// primeiro numa fila, e era justamente o que a lista não mostrava.
//
// A cor sobe junto com a espera: sem cor até 5 minutos (nada acontecendo),
// e daí em diante cada faixa é um aviso mais forte. Só o tempo recebe cor
// na linha — canal e conexão são identidade, não urgência.
const FAIXAS = [
  { ateMinutos: 5, classe: "text-muted-foreground" },
  { ateMinutos: 15, classe: "text-sky-600 dark:text-sky-400" },
  { ateMinutos: 60, classe: "text-amber-600 dark:text-amber-400" },
  { ateMinutos: Infinity, classe: "text-rose-600 dark:text-rose-400" },
];

export const minutosDesde = (data) => {
  if (!data) return 0;

  return Math.max(
    0,
    Math.floor((Date.now() - new Date(data).getTime()) / 60000),
  );
};

export const formatarEspera = (minutos) => {
  if (minutos < 1) return "agora";
  if (minutos < 60) return `${minutos} min`;

  const horas = Math.floor(minutos / 60);

  if (horas < 24) {
    const resto = minutos % 60;
    return resto ? `${horas}h ${resto}min` : `${horas}h`;
  }

  const dias = Math.floor(horas / 24);

  return dias === 1 ? "1 dia" : `${dias} dias`;
};

export const classeDaEspera = (minutos) =>
  FAIXAS.find((f) => minutos < f.ateMinutos).classe;

// Só faz sentido cobrar espera de quem ainda não foi atendido; num ticket
// em andamento o relógio não é uma dívida.
//
// A conta é feita sobre `aguardandoDesde` — a data da última mensagem DO
// CLIENTE, que a listagem calcula. O `updatedAt` só serve de reserva: qualquer
// coisa que o sistema faça no atendimento (um ack chegando, uma tag) o zera, e
// a lista passava a dizer "agora" para quem estava esperando há uma hora.
export const esperaDoTicket = (ticket) => {
  if (!ticket || ticket.status === "closed") return null;

  const minutos = minutosDesde(ticket.aguardandoDesde || ticket.updatedAt);

  return {
    minutos,
    rotulo: formatarEspera(minutos),
    classe:
      ticket.status === "pending"
        ? classeDaEspera(minutos)
        : "text-muted-foreground",
  };
};

// Prazo desta fila para responder. Sem SLA configurado, meia hora — é o limiar
// a partir do qual silêncio no WhatsApp já é percebido como abandono.
const LIMIAR_PADRAO = 30;

// "Falta responder": o cliente falou por último e ninguém respondeu dentro do
// prazo. Diferente de espera: um atendimento em andamento também pode estar
// devendo resposta.
export const faltaResponder = (ticket) => {
  if (!ticket || ticket.status === "closed" || !ticket.aguardandoDesde) {
    return null;
  }

  const minutos = minutosDesde(ticket.aguardandoDesde);
  const limiar = Number(ticket.queue?.slaMinutes) || LIMIAR_PADRAO;

  if (minutos < limiar) return null;

  return { minutos, rotulo: formatarEspera(minutos), limiar };
};

// SLA prestes a vencer: o aviso que chega ANTES do prejuízo.
//
// "Falta responder" já cobra o prazo estourado; este cobre a janela final,
// dos 80% da meta até ela. Feito na tela, como a espera: o worker da escalada
// roda de 5 em 5 minutos, e um aviso de "vence em 2min" que chega 5 minutos
// atrasado não avisa nada.
//
// Exige meta CONFIGURADA na fila — diferente de `faltaResponder`, que cai num
// padrão de 30min. O padrão serve para cobrar o que já passou; anunciar
// vencimento de um prazo que ninguém definiu seria inventar urgência.
const INICIO_DO_AVISO = 0.8;

export const slaVencendo = (ticket) => {
  if (!ticket || ticket.status === "closed" || !ticket.aguardandoDesde) {
    return null;
  }

  const meta = Number(ticket.queue?.slaMinutes);

  if (!meta) return null;

  const minutos = minutosDesde(ticket.aguardandoDesde);

  // Passou do prazo: quem fala daqui em diante é o "Falta responder".
  if (minutos < meta * INICIO_DO_AVISO || minutos >= meta) return null;

  const restam = Math.max(1, Math.ceil(meta - minutos));

  return { restam, meta, rotulo: `Vence em ${restam}min` };
};

// Sinal da escalada de SLA: qual nível da fila este atendimento já alcançou.
//
// Quem aplica é o worker (`ApplyTicketRulesService`), que grava o número de
// níveis vencidos em `slaEscalationLevel`. A cor e o rótulo ficam na fila, não
// no atendimento: mudar o nível na fila repinta o que já está na lista, sem
// migração de dados.
export const nivelDeEscalada = (ticket) => {
  const nivel = ticket?.slaEscalationLevel;

  if (!nivel || ticket.status === "closed") return null;

  // O nível é 1-based: "1" é o primeiro da lista.
  return ticket.queue?.slaEscalations?.[nivel - 1] || null;
};

// A pausa do atendimento, do jeito que a tela precisa dela.
//
// Três estados, porque são três coisas diferentes para quem atende:
// esperando a hora (azul), o cliente escreveu antes da hora (amarelo — o
// compromisso continua, mas já há o que ler) e o prazo venceu sem ninguém
// voltar (vermelho, e é este que pisca, toca e avisa).
//
// O vencido continua sendo devolvido — antes virava `null`, e com isso a tela
// não tinha como cobrar o retorno prometido.
export const estadoDaPausa = (ticket) => {
  if (!ticket?.snoozedUntil) return null;

  const quando = new Date(ticket.snoozedUntil);
  const vencido = quando.getTime() <= Date.now();
  // Marcador do servidor, não o contador de não lidas: abrir a conversa zera
  // o contador, e o relógio voltava ao azul sem ninguém ter respondido.
  const comMensagem = !vencido && Boolean(ticket.snoozeClienteEscreveu);

  return {
    quando,
    vencido,
    comMensagem,
    rotulo: quando.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    }),
    // A cor diz o estado sem ninguém ter de ler a data.
    classe: vencido
      ? "text-rose-600 dark:text-rose-400"
      : comMensagem
        ? "text-amber-600 dark:text-amber-400"
        : "text-sky-600 dark:text-sky-400"
  };
};

// Pausa ainda valendo. Continua existindo porque a listagem esconde o que está
// pausado até a hora, e para isso o vencido não conta.
export const adiadoAte = (ticket) => {
  const pausa = estadoDaPausa(ticket);

  return pausa && !pausa.vencido ? pausa : null;
};

// Um único relógio para todo mundo que mostra espera. Cada tela tinha o seu
// intervalo, e a lista chegava a marcar 16h24 enquanto o cabeçalho ainda dizia
// 16h18 — o mesmo atendimento com dois tempos diferentes na mesma janela.
// Como as contas acima são feitas a cada render, basta que o redesenho aconteça
// na mesma batida para os números baterem.
const BATIDA = 30000;

const inscritos = new Set();
let cronometro = null;

export const useTickDeEspera = () => {
  const [, redesenhar] = useState(0);

  useEffect(() => {
    const avisar = () => redesenhar((n) => n + 1);

    inscritos.add(avisar);

    if (!cronometro) {
      cronometro = setInterval(() => {
        inscritos.forEach((fn) => fn());
      }, BATIDA);
    }

    return () => {
      inscritos.delete(avisar);

      if (!inscritos.size && cronometro) {
        clearInterval(cronometro);
        cronometro = null;
      }
    };
  }, []);
};
